import type { PlayEvent, Timeline } from './timeline';
import { clipExpressionEnvelope } from '@/music/score/expression';

/** Absolute timeline beats (buildTimeline's unit); the end is exclusive. */
export interface ScoreAuditionRange {
  fromBeat: number;
  toBeat: number;
}

/** Bound an audition to the written score, rejecting empty or invalid spans. */
export function normalizeAuditionRange(
  totalBeats: number,
  range?: ScoreAuditionRange,
): ScoreAuditionRange | null {
  if (!Number.isFinite(totalBeats) || totalBeats <= 0) return null;
  const requested = range ?? { fromBeat: 0, toBeat: totalBeats };
  if (!Number.isFinite(requested.fromBeat) || !Number.isFinite(requested.toBeat)) return null;
  const fromBeat = Math.max(0, Math.min(requested.fromBeat, totalBeats));
  const toBeat = Math.max(0, Math.min(requested.toBeat, totalBeats));
  return toBeat > fromBeat ? { fromBeat, toBeat } : null;
}

/** A contiguous written passage, with zero-based inclusive bar indices.
 * The timeline's own grid preserves pickups and changes of time signature. */
export function barAuditionRange(
  timeline: Pick<Timeline, 'barStarts' | 'totalBeats'>,
  firstBarIndex: number,
  lastBarIndex = firstBarIndex,
): ScoreAuditionRange | null {
  if (
    !Number.isInteger(firstBarIndex) ||
    !Number.isInteger(lastBarIndex) ||
    firstBarIndex < 0 ||
    lastBarIndex < firstBarIndex ||
    lastBarIndex >= timeline.barStarts.length
  ) {
    return null;
  }
  return normalizeAuditionRange(timeline.totalBeats, {
    fromBeat: timeline.barStarts[firstBarIndex],
    toBeat: timeline.barStarts[lastBarIndex + 1] ?? timeline.totalBeats,
  });
}

/** Clip every intersecting voice to a range without moving the score's grid.
 * A tied/sustained note that began earlier attacks at the audition start and
 * sounds only for its remaining intersection with the selected passage. */
export function clipAuditionEvents(
  events: readonly PlayEvent[],
  range: ScoreAuditionRange,
): PlayEvent[] {
  return events.flatMap((event) => {
    const absBeat = Math.max(range.fromBeat, event.absBeat);
    const endBeat = Math.min(
      range.toBeat, event.absBeat + event.durBeats * (event.articulationGate ?? 1),
    );
    if (endBeat <= absBeat) return [];
    const clipped = clipExpressionEnvelope(
      event.expression, absBeat - event.absBeat, endBeat - event.absBeat,
    );
    const next: PlayEvent = { ...event, absBeat, durBeats: endBeat - absBeat };
    // The sounding gate was applied before intersecting the audition; do not
    // shorten it a second time, or restart a staccato whose sound has ended.
    if (event.articulationGate !== undefined) next.articulationGate = 1;
    if (event.expression) {
      next.velocity = (event.velocity ?? 1) * clipped.gain;
      if (clipped.expression) next.expression = clipped.expression;
      else delete next.expression;
    }
    return [next];
  });
}
